import React, { useEffect, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';

import {
  getDepartureTime,
  getArrivalTime,
  getACReg,
  getMelAmount,
  getMelItemsWithLinks,
  timeConvert,
} from '../utilities/helperfunctions';
import { formatStationTime, zoneForIcao } from '../utilities/stationLocalTime';
import norweiganIcon from './../static/RGB-Norwegian-icon.svg';
import mapIcon from './../static/map-location-dot-solid.svg';
import { Logout } from '../components';
import { formatUnixTimestamp, isTrainingMode } from '../helpers';
import { CTOTModal } from './CTOTModal';
import './../style/top-menu.scss';


export function TopMenu(props) {
  const [openMEL, setOpenMEL] = useState(false);
  const [openCTOT, setOpenCTOT] = useState(false);
  const [securitySearchKey, setSecuritySearchKey] = useState(0); // Used for restarting security search animation.

  const restartAnimation = () => {
    setSecuritySearchKey((prevKey) => prevKey + 1);
  };

  const handleCTOTClick = () => {
    setOpenCTOT(true);
    if (props.onCtotAcknowledge) {
      props.onCtotAcknowledge();
    }
  };

  const legacySecuritySearch = props.data?.sSecSearch?.[0];
  const securitySearchInfo = props.data?.sSecSearchCheckInfo?.[0]
    ? props.data.sSecSearchCheckInfo[0].replace(/<br\s*\/?>/gi, '\n').trim()
    : legacySecuritySearch
      ? `${legacySecuritySearch}\nSECURITY SEARCH REQUIRED`
      : '';

  const departureGroupTalk = props.data?.bDep_GroupTalk[0] === 'true' ?? false;
  const destinationGroupTalk =
    props.data?.bDest_GroupTalk[0] === 'true' ?? false;

  const melAmount = props.data?.sABC_MEL?.[0]
    ? getMelAmount(props.data.sABC_MEL[0])
    : 0;
  const melItems = getMelItemsWithLinks(props.data || '') || [];
  const trainingMode = isTrainingMode();
  const trainingMelText = 'MEL: This topic will be addressed during the briefing.\nABC: This is simulator-specific and will be covered during the briefing.';

  const departureIcao = props.data?.sDepICAO?.[0];
  const arrivalIcao = props.data?.sDestICAO?.[0];

  // Helper functions that format data
  const departureUtc = getDepartureTime(props.data || '');
  const arrivalUtc = getArrivalTime(props.data || '');
  const departureLtLabel = zoneForIcao(departureIcao)
    ? formatStationTime(props.data?.dtSTD?.[0], departureIcao, 'lt') || 'N/A'
    : 'N/A';
  const arrivalLtLabel = zoneForIcao(arrivalIcao)
    ? formatStationTime(props.data?.dtSTA?.[0], arrivalIcao, 'lt') || 'N/A'
    : 'N/A';
  const ACReg = getACReg(props.data || '') || 'N/A';
  const flightTime =
    props.data?.dbEstFlightTime?.[0] && props.data?.dbEstFlightTime?.[0] > 0
      ? timeConvert(props.data.dbEstFlightTime[0])
      : 'N/A';

  // Restart animation on flight change
  useEffect(() => {
    restartAnimation();
  }, [props.data?.sComFltNo]);


  const departureParkLabel = `Park ${props.flightInfoCrew?.DepartureStand?.[0]
    ? props.flightInfoCrew.DepartureStand[0]
    : 'N/A'
    }`;

  const arrivalParkLabel = `Park ${props.flightInfoCrew?.ArrivalStand?.[0]
    ? props.flightInfoCrew.ArrivalStand[0]
    : 'N/A'
    }`;

  const departureGateLabel = `Gate ${props.flightInfoCrew?.DepartureGate?.[0]
    ? props.flightInfoCrew.DepartureGate[0]
    : 'N/A'
    } ${departureGroupTalk ? '(GT)' : ''}`;

  const arrivalGateLabel = `Gate ${props.flightInfoCrew?.ArrivalGate?.[0]
    ? props.flightInfoCrew.ArrivalGate[0]
    : 'N/A'
    } ${destinationGroupTalk ? '(GT)' : ''}`;

  const updateLabel = `Upd: ${(props.dataFetchTimestamp
    ? formatUnixTimestamp(props.dataFetchTimestamp, 'DDMMMHH:mm')
    : 'N/A'
  ).toUpperCase()}`;

  const callSignLabel = `ATC C/S ${props.data?.sCallSign ? props.data.sCallSign : 'N/A'
    }`;

  const ofpTimestamp = props.data?.dtFPTimestamp?.[0]
    ? props.data.dtFPTimestamp[0].slice(0, 19).replace(/[\T,\Z]/g, ' ')
    : 'N/A';

  const departureAirportLabel = props.isCabinCrew
    ? (props.data?.sDepIATA?.[0] ?? 'N/A')
    : (props.data?.sDepICAO?.[0] ?? 'N/A');
  const destinationAirportLabel = props.isCabinCrew
    ? (props.data?.sDestIATA?.[0] ?? 'N/A')
    : (props.data?.sDestICAO?.[0] ?? 'N/A');

  return (
    <div className='top-menu'>
      {trainingMode && (
        <div className='training-mode-label' role='status'>
          Training Mode
        </div>
      )}
      {/* Open mel popup if openMEL === true */}
      {openMEL && (
        <>
          <div
            className='overlay-top-menu'
            onClick={() => setOpenMEL((prevState) => !prevState)}
          ></div>
          <div className='mel-popup'>
            <div
              className='closeRowTopMenu'
              onClick={() => setOpenMEL((prevState) => !prevState)}
            >
              <CloseIcon
                fontSize='medium'
                sx={[{ '&hover': { cursor: 'pointer' } }]}
              />
            </div>
            {trainingMode ? (
              <pre className='mel-text'>{trainingMelText}</pre>
            ) : Array.isArray(melItems) ? (
              melItems.map((item, index) => {
                const { rawText, melRef, link } = item;
                const textSplit = rawText.split('DEADLINE');
                const preDeadline = textSplit?.[0] || 'N/A';
                const postDeadline = textSplit?.[1] || 'N/A';

                if (link && melRef) {
                  const parts = postDeadline.split(`"${melRef}"`);
                  if (parts.length >= 2) {
                return (
                  <pre className='mel-text' key={index}>
                        {preDeadline}{' '}
                        <b>
                          DEADLINE{parts[0]}
                          <a
                            href={link}
                            style={{
                              color: '#D81939',
                              textDecoration: 'underline',
                              cursor: 'pointer',
                            }}
                          >
                            "{melRef}"
                          </a>
                          {parts.slice(1).join(`"${melRef}"`)}
                        </b>
                      </pre>
                    );
                  }
                }

                return (
                  <pre className='mel-text' key={index}>
                    {preDeadline}{' '}
                    <b>DEADLINE{postDeadline}</b>
                  </pre>
                );
              })
            ) : (
              <pre className='mel-text'>N/A</pre>
            )}
          </div>
        </>
      )}

      <CTOTModal
        isOpen={openCTOT}
        onClose={() => setOpenCTOT(false)}
        ctotData={props.ctotCrew}
      />

      <div className='page-state-row'>
        <Logout />
        <p>{props.pageTitle}</p>
      </div>

      <div className='row justify-space-between'>
        <div className='column'>
          <div className='row'>
            <p className='bigBoldText'>
              {props.data?.sComFltNo?.[0] ? props.data.sComFltNo[0] : 'N/A'}
            </p>
            <p
              className='bigBoldText'
              style={{ color: 'grey', marginLeft: '12px' }}
            >
              {callSignLabel}
            </p>
          </div>
          <div className='column'>
            <div className='flight-clocks'>
              <div>
              <p className='bigBoldText' style={{ color: '#D81939' }}>
                  {departureUtc || 'N/A'}
              </p>
                <p className='mediumBoldText' style={{ color: '#D81939' }}>
                  {departureLtLabel}
              </p>
            </div>
              <p className='bigBoldText'>
                <a
                  href={`docunet://NAX-ABC-${props.data?.sDepICAO?.[0]}`}
                  className='underlined-link'
                  target='_blank'
                  rel='noopener noreferrer'
                  style={{ textDecoration: 'underline', color: 'inherit' }}
                >
                  {departureAirportLabel}
                </a>
                {' - '}
                <a
                  href={`docunet://NAX-ABC-${props.data?.sDestICAO?.[0]}`}
                  className='underlined-link'
                  target='_blank'
                  rel='noopener noreferrer'
                  style={{ textDecoration: 'underline', color: 'inherit' }}
                >
                  {destinationAirportLabel}
                </a>
              </p>
              <div className='flight-clocks__arrival'>
                <div className='flight-clocks__line'>
              <p className='bigBoldText' style={{ color: '#D81939' }}>
                {arrivalUtc || 'N/A'}
              </p>
              <p style={{ fontSize: '8px' }}>(UTC)</p>
            </div>
                <div className='flight-clocks__line'>
                  <p className='mediumBoldText' style={{ color: '#D81939' }}>
                    {arrivalLtLabel}
                  </p>
                  <p style={{ fontSize: '8px' }}>(LT)</p>
                </div>
              </div>
            </div>
            <div className='row justify-space-between'>
              <p className='mediumRegularTextText'>{departureParkLabel}</p>
              <p className='mediumRegularTextText'>{arrivalParkLabel}</p>
            </div>
            <div className='row justify-space-between'>
              <p className='mediumRegularTextText'>{departureGateLabel}</p>
              <p className='mediumRegularTextText'>{arrivalGateLabel}</p>
            </div>
          </div>
          <div className='row'>
            <p className='mediumBoldText'>CTOT</p>
            <p
              className='mediumBoldText'
              style={{
                color: '#D81939',
                cursor: 'pointer',
                textDecoration: 'underline',
              }}
              onClick={handleCTOTClick}
            >
              {props.ctot || 'N/A'}
              {props.ctotHasChanged && (
                <span className='ctot-new-indicator'>NEW</span>
              )}
            </p>
            <p className='mediumRegularTextText' style={{ marginLeft: 'auto' }}>
              FLT: {flightTime}
            </p>
          </div>
          <div className='row center space-right'>
            <p className='bigBoldText'>{ACReg}</p>
            <p>{props.data?.sACType || 'N/A'}</p>
              <p
                className='smallBoldText'
                onClick={() => setOpenMEL((prevState) => !prevState)}
                style={{ color: '#D81939', marginBottom: '2px' }}
              >
              ({melAmount} {melAmount === 1 ? 'ATL' : 'ATLs'})
              </p>
          </div>
          <div className='row'>
            <p className='smallBoldText'>
              FPVer: {props.data?.sFPVer ?? 'N/A'}
            </p>
          </div>
          <div className='row'>
            <p className='smallBoldText'>FP Time Stamp: {ofpTimestamp}</p>
          </div>
          <div className='row'>
            <p className='smallBoldText'>{updateLabel}</p>
            <p style={{ fontSize: '8px' }}>(LT)</p>
          </div>
          <div className='row'>
            {securitySearchInfo && (
              <>
                <div className='row'>
                  <span className='smallBlinkingText' key={securitySearchKey}>
                    {securitySearchInfo}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>

        <div className='column' style={{ justifyContent: 'space-between' }}>
          <div className='row' style={{ marginTop: '7px' }}>
            {!props.isCabinCrew && (
              <img
                className='mapIcon'
                src={mapIcon}
                onClick={props.navigateToMap}
                alt=''
              />
            )}
            <img className='norwegian-icon' src={norweiganIcon} alt='' />
          </div>
        </div>
      </div>
    </div>
  );
}
